import { useDroppable } from '@dnd-kit/core';
import Image from 'components/Basic/Image/Image';
import { Webline } from 'components/Layout/Webline/Webline';
import { ConfiguratorProductInDrawer } from 'components/Pages/Configurator/ConfiguratorProductInDrawer';
import { createEmptyArray } from 'helpers/arrayUtils';
import { DRAWER_DROP_AREA_ID } from 'helpers/configurator/configuratorConstants';
import useTranslation from 'next-translate/useTranslation';
import DrawerBottomImage from 'public/images/drawer-bottom.png';
import { useEffect, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';

export const ConfiguratorDrawer: FC = () => {
    const { t } = useTranslation();
    const { drawerSizes, updateConfiguratorState, productsInDrawer, drawerPaddings } = usePersistStore();
    const { setNodeRef } = useDroppable({
        id: DRAWER_DROP_AREA_ID,
    });

    const drawerAreaInCells = drawerSizes.length * drawerSizes.width;
    const [cellElement, setCellElement] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        const updateActualCellWidth = () => {
            if (cellElement) {
                updateConfiguratorState({ actualCellWidthInPixels: cellElement.getBoundingClientRect().width });
            }
        };
        updateActualCellWidth();

        window.addEventListener('resize', updateActualCellWidth, true);

        return () => {
            window.removeEventListener('resize', updateActualCellWidth, true);
        };
    }, [updateConfiguratorState, drawerAreaInCells, cellElement]);

    const paddingInDrawer = (() => {
        const drawerTotalWidth = drawerSizes.width + drawerPaddings.widthPadding;
        const drawerTotalLength = drawerSizes.length + drawerPaddings.lengthPadding;

        const widthPaddingInPercents = (drawerPaddings.widthPadding / drawerTotalWidth) * 100;
        const lengthPaddingInPercents = (drawerPaddings.lengthPadding / drawerTotalLength) * 100;

        return { widthPadding: widthPaddingInPercents / 2, lengthPadding: lengthPaddingInPercents / 2 };
    })();

    return (
        <Webline>
            <div className="flex w-full flex-wrap bg-[url('/images/drawer-middle.png')] bg-[length:100%_100%] bg-no-repeat px-[10%]">
                <div
                    className="relative grid w-full justify-center"
                    ref={setNodeRef}
                    style={{
                        gridTemplateColumns: `repeat(${drawerSizes.width}, minmax(0, 1fr))`,
                        margin: `${paddingInDrawer.lengthPadding}% ${paddingInDrawer.widthPadding}%`,
                    }}
                >
                    {createEmptyArray(drawerAreaInCells).map((_, index) => (
                        <div
                            key={index}
                            className="aspect-ratio-fallback aspect-square max-w-md border border-grayDarker"
                            ref={(node) => setCellElement(node)}
                        />
                    ))}
                    {productsInDrawer.map((product) => (
                        <ConfiguratorProductInDrawer key={product.id} product={product} />
                    ))}
                </div>
            </div>
            <Image alt={t('Drawer bottom')} className="w-full -translate-y-[1px]" src={DrawerBottomImage} />
        </Webline>
    );
};
